import React, { useEffect, useState, useCallback } from "react";
import { pickPortfolio } from "./selectedPortfolio";
import { API } from "./shared";
import { useNavigate, useSearchParams } from "react-router-dom";
import PeriodChips from "@/components/PeriodChips";
import { nextPeriodNames } from "@/lib/periodSuggestions";
import {
  Loader2, Plus, CalendarRange, Send, Rocket, RotateCcw, Lock,
  RefreshCw, Trash2, ChevronDown, ChevronRight, Clock, CheckCircle2, CircleDashed, ClipboardPen, GitCompare,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  open: { label: "Open", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  closed: { label: "Closed", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

const REQ_BADGE = {
  requested: { label: "Requested", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  started: { label: "In Progress", cls: "bg-blue-50 text-blue-700 border-blue-200", icon: CircleDashed },
  completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
};

const PA_BADGE = {
  not_started: { label: "Not started", cls: "bg-slate-100 text-slate-500 border-slate-200", icon: CircleDashed },
  started: { label: "In Progress", cls: "bg-blue-50 text-blue-700 border-blue-200", icon: CircleDashed },
  completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  name: "", cadence: "one_off", recurrence_interval: "annual",
  start_date: todayISO(), due_date: "", school_ids: null,
  enable_portfolio_assessments: true,
});

export default function DiagnosticPeriods() {
  const navigate = useNavigate();
  const [pid, setPid] = useState(null);
  const [pname, setPname] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async (portfolioId) => {
    const r = await fetch(`${API}/api/portfolio/${portfolioId}/periods`, { credentials: "include" });
    if (r.ok) setData(await r.json());
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/portfolio/me`, { credentials: "include" });
        const me = await r.json();
        const p = pickPortfolio(me.portfolios);
        if (!p) { setLoading(false); return; }
        setPid(p.id); setPname(p.name);
        await load(p.id);
      } finally { setLoading(false); }
    })();
  }, [load]);

  const openCreate = () => {
    const all = (data?.schools || []).map((s) => s.tenant_id);
    setForm({ ...emptyForm(), school_ids: all });
    setShowCreate(true);
  };

  // Deep link: ?new=1&school=<tenant_id>[&pid=][&from=dashboard|schools] opens New period with only that school.
  const [params, setParams] = useSearchParams();
  const [returnTo, setReturnTo] = useState(null);
  useEffect(() => {
    if (!data || params.get("new") !== "1") return;
    const tid = params.get("school");
    const from = params.get("from");
    setReturnTo(from === "dashboard" ? `/portfolio/dashboard?pid=${pid}` : from === "schools" ? `/portfolio/schools?pid=${pid}` : null);
    setForm({ ...emptyForm(), school_ids: tid ? [tid] : (data.schools || []).map((s) => s.tenant_id) });
    setShowCreate(true);
    setParams({}, { replace: true });
  }, [data, params, setParams, pid]);

  const toggleSchool = (tid) => {
    setForm((f) => {
      const cur = f.school_ids || [];
      return { ...f, school_ids: cur.includes(tid) ? cur.filter((x) => x !== tid) : [...cur, tid] };
    });
  };

  const submitCreate = async (launch = false) => {
    if (!form.name.trim()) { toast.error("Enter a period name"); return; }
    if (!form.school_ids || form.school_ids.length === 0) { toast.error("Select at least one school"); return; }
    const n = form.school_ids.length;
    if (launch && !window.confirm(`Create "${form.name.trim()}" and email ${n} school${n === 1 ? "" : "s"} now?`)) return;
    setBusy(launch ? "launch-new" : "create");
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/periods`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(), cadence: form.cadence,
          recurrence_interval: form.cadence === "recurring" ? form.recurrence_interval : null,
          start_date: form.start_date || null, due_date: form.due_date || null,
          school_ids: form.school_ids,
          enable_portfolio_assessments: form.enable_portfolio_assessments,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not create period");
      if (launch) {
        const lr = await fetch(`${API}/api/portfolio/${pid}/periods/${d.period_id}/launch`, { method: "POST", credentials: "include" });
        const ld = await lr.json();
        if (!lr.ok) { toast.error(`Period saved as draft, but launch failed: ${ld.detail || "error"}`); setShowCreate(false); load(pid); return; }
        toast.success(ld.notified ? `Period launched — ${ld.notified} email(s) sent to schools` : "Period launched — no school users to notify yet");
      } else {
        toast.success("Period created as draft — launch it to notify schools");
      }
      setShowCreate(false);
      if (returnTo) { navigate(returnTo); return; }
      load(pid);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const act = async (period, action, label) => {
    setBusy(`${action}-${period.id}`);
    try {
      const method = action === "delete" ? "DELETE" : "POST";
      const path = action === "delete" ? "" : `/${action}`;
      const r = await fetch(`${API}/api/portfolio/${pid}/periods/${period.id}${path}`, { method, credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Action failed");
      if (action === "launch") toast.success(`Requests pushed — ${d.notified || 0} email(s) sent to schools`);
      else if (action === "open-next") toast.success("Next cycle created as draft — set dates and launch");
      else toast.success(label || "Done");
      load(pid);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const resend = async (period, tenantId) => {
    setBusy(`resend-${period.id}-${tenantId}`);
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/periods/${period.id}/requests/${tenantId}/resend`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not resend");
      toast.success(`Reminder sent (${d.notified || 0})`);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const completePortfolioAssessment = async (period, tenantId) => {
    setBusy(`pa-${period.id}-${tenantId}`);
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/periods/${period.id}/schools/${tenantId}/portfolio-assessment`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not open the portfolio assessment");
      localStorage.setItem("diag_session", JSON.stringify({
        user: d.user, tenant: d.tenant, session_token: d.session_token,
        acting_as: "portfolio", access: d.access, portfolio_context: d.portfolio_context,
        loginAt: new Date().toISOString(),
      }));
      window.location.assign(`/diagnostic/form?diag=${d.diagnostic_id}`);
    } catch (e) { toast.error(e.message); setBusy(""); }
  };

  if (loading) return <div className="py-16 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (!pid) return <div className="py-16 text-center text-slate-500" data-testid="periods-no-portfolio">No portfolio found for your account.</div>;

  const canManage = data?.can_manage;
  const periods = data?.periods || [];
  const schools = data?.schools || [];

  return (
    <div data-testid="diagnostic-periods-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="text-xl font-bold text-slate-900">Diagnostic Periods</h1>
        {canManage && (
          <button onClick={openCreate} data-testid="period-create-open"
            className="inline-flex items-center gap-2 rounded-md bg-[#007a9e] hover:bg-[#00607d] text-white text-sm font-medium px-4 py-2">
            <Plus className="h-4 w-4" /> New Period
          </button>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-5">Request diagnostics across the schools in <strong>{pname}</strong> — create a one-off or recurring period, then launch it to notify each school.</p>

      <div className="space-y-4">
        {periods.map((p) => {
          const sb = STATUS_BADGE[p.status] || STATUS_BADGE.draft;
          const isOpen = expanded[p.id];
          const total = (p.progress.requested || 0) + (p.progress.started || 0) + (p.progress.completed || 0);
          return (
            <div key={p.id} data-testid={`period-row-${p.id}`} className="rounded-xl border border-slate-200 bg-white">
              <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="h-9 w-9 shrink-0 rounded-lg grid place-items-center bg-red-50 text-red-600"><CalendarRange className="h-4 w-4" /></div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{p.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${sb.cls}`}>{sb.label}</span>
                      {p.cadence === "recurring" && (
                        <span className="text-xs px-2 py-0.5 rounded-full border bg-violet-50 text-violet-700 border-violet-200">
                          Recurring · {p.recurrence_interval || "cycle"} · #{p.cycle_number}
                        </span>
                      )}
                    </div>
                    <div className="text-[13px] text-slate-500 mt-0.5">
                      {p.school_count} school{p.school_count === 1 ? "" : "s"}
                      {p.due_date ? <> · due <strong>{p.due_date}</strong></> : null}
                      {total > 0 && <> · {p.progress.completed || 0}/{total} completed</>}
                    </div>
                  </div>
                </div>

                {total > 0 && (
                  <div className="flex items-center gap-2 text-xs shrink-0">
                    <span className="inline-flex items-center gap-1 text-amber-700"><Clock className="h-3.5 w-3.5" />{p.progress.requested || 0}</span>
                    <span className="inline-flex items-center gap-1 text-blue-700"><CircleDashed className="h-3.5 w-3.5" />{p.progress.started || 0}</span>
                    <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />{p.progress.completed || 0}</span>
                  </div>
                )}

                <div className="flex items-center flex-wrap gap-2 lg:shrink-0">
                  {p.enable_portfolio_assessments && p.status !== "draft" && (
                    <button onClick={() => navigate(`/portfolio/compare/${p.id}`)} data-testid={`period-compare-${p.id}`}
                      className="text-xs rounded-md border border-slate-300 hover:bg-slate-50 px-3 py-1.5 inline-flex items-center gap-1.5 font-medium text-slate-700">
                      <GitCompare className="h-3.5 w-3.5" /> Compare
                    </button>
                  )}
                  {canManage && p.status === "draft" && (
                    <button onClick={() => act(p, "launch")} disabled={busy === `launch-${p.id}`} data-testid={`period-launch-${p.id}`}
                      className="text-xs rounded-md bg-[#0b1220] hover:bg-[#1e293b] text-white px-3 py-1.5 inline-flex items-center gap-1.5 font-medium">
                      {busy === `launch-${p.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />} Launch
                    </button>
                  )}
                  {canManage && p.status === "open" && (
                    <button onClick={() => act(p, "close", "Period closed")} disabled={busy === `close-${p.id}`} data-testid={`period-close-${p.id}`}
                      className="text-xs rounded-md border border-slate-200 hover:bg-slate-50 px-3 py-1.5 inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Close</button>
                  )}
                  {canManage && p.status === "closed" && (
                    <button onClick={() => act(p, "reopen", "Period reopened")} disabled={busy === `reopen-${p.id}`} data-testid={`period-reopen-${p.id}`}
                      className="text-xs rounded-md border border-slate-200 hover:bg-slate-50 px-3 py-1.5 inline-flex items-center gap-1.5"><RotateCcw className="h-3.5 w-3.5" /> Reopen</button>
                  )}
                  {canManage && p.cadence === "recurring" && p.status !== "draft" && (
                    <button onClick={() => act(p, "open-next")} disabled={busy === `open-next-${p.id}`} data-testid={`period-opennext-${p.id}`}
                      className="text-xs rounded-md border border-violet-200 text-violet-700 hover:bg-violet-50 px-3 py-1.5 inline-flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> Open next cycle</button>
                  )}
                  {canManage && (p.status === "draft" || p.status === "closed") && (
                    <button onClick={() => { if (window.confirm(`Delete "${p.name}"? This removes the period and its request records permanently.`)) act(p, "delete", "Period deleted"); }} disabled={busy === `delete-${p.id}`} data-testid={`period-delete-${p.id}`}
                      className="text-slate-400 hover:text-red-600" title="Delete period"><Trash2 className="h-4 w-4" /></button>
                  )}
                  <button onClick={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))} data-testid={`period-expand-${p.id}`}
                    className="text-slate-400 hover:text-slate-700">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-slate-100 px-4 py-3 space-y-2" data-testid={`period-requests-${p.id}`}>
                  {(p.requests || []).length === 0 && <p className="text-sm text-slate-400">Not launched yet — no requests sent.</p>}
                  {p.enable_portfolio_assessments && (p.requests || []).length > 0 && (
                    <div className="hidden sm:flex items-center justify-end gap-6 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      <span className="w-28 text-right">School</span>
                      <span className="w-28 text-right">Portfolio</span>
                    </div>
                  )}
                  {(p.requests || []).map((req) => {
                    const rb = REQ_BADGE[req.status] || REQ_BADGE.requested;
                    const RIcon = rb.icon;
                    const pab = PA_BADGE[req.portfolio_status] || PA_BADGE.not_started;
                    const PIcon = pab.icon;
                    return (
                      <div key={req.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm" data-testid={`period-req-${p.id}-${req.tenant_id}`}>
                        <span className="text-slate-700 truncate sm:flex-1 sm:min-w-[8rem]">{req.school_name}</span>
                        <div className="flex items-center flex-wrap gap-x-3 gap-y-2 sm:justify-end">
                          <span className={`w-28 justify-end text-xs px-2 py-0.5 rounded-full border inline-flex items-center gap-1 whitespace-nowrap ${rb.cls}`}><RIcon className="h-3 w-3 shrink-0" />{rb.label}</span>
                          {p.enable_portfolio_assessments && (
                            <span className={`w-28 justify-end text-xs px-2 py-0.5 rounded-full border inline-flex items-center gap-1 whitespace-nowrap ${pab.cls}`} data-testid={`period-pa-status-${p.id}-${req.tenant_id}`}><PIcon className="h-3 w-3 shrink-0" />{pab.label}</span>
                          )}
                          {p.enable_portfolio_assessments && canManage && p.status === "open" && req.portfolio_status !== "completed" && (
                            <button onClick={() => completePortfolioAssessment(p, req.tenant_id)} disabled={busy === `pa-${p.id}-${req.tenant_id}`} data-testid={`period-portfolio-assess-${p.id}-${req.tenant_id}`}
                              className="text-xs text-[#b45309] hover:underline inline-flex items-center gap-1 whitespace-nowrap">
                              {busy === `pa-${p.id}-${req.tenant_id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardPen className="h-3.5 w-3.5" />}
                              {req.portfolio_status === "started" ? "Continue portfolio review" : "Complete portfolio assessment"}
                            </button>
                          )}
                          {canManage && p.status === "open" && req.status !== "completed" && (
                            <button onClick={() => resend(p, req.tenant_id)} disabled={busy === `resend-${p.id}-${req.tenant_id}`} data-testid={`period-resend-${p.id}-${req.tenant_id}`}
                              className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1">
                              {busy === `resend-${p.id}-${req.tenant_id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Remind
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {periods.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="periods-empty">
            <CalendarRange className="h-8 w-8 mx-auto text-slate-300" />
            <p className="mt-3 text-sm">No diagnostic periods yet. Create one to request diagnostics from your schools.</p>
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowCreate(false)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="period-create-modal">
            <h2 className="text-lg font-bold text-slate-900">New Diagnostic Period</h2>
            <p className="text-[13px] text-slate-500 mt-1">Save it as a draft to launch later, or launch now to notify each selected school right away.</p>

            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Period name *</label>
                <input id="period-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Fall 2026"
                  data-testid="period-name" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
                <PeriodChips names={nextPeriodNames((data?.periods || []).map((p) => p.name))} value={form.name}
                  onPick={(n) => setForm((f) => ({ ...f, name: n }))} inputId="period-name-input" testPrefix="period-name-chip" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">Type</label>
                  <select value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })}
                    data-testid="period-cadence" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white">
                    <option value="one_off">One-off</option>
                    <option value="recurring">Recurring</option>
                  </select>
                </div>
                {form.cadence === "recurring" && (
                  <div>
                    <label className="text-xs font-medium text-slate-500">Cadence</label>
                    <select value={form.recurrence_interval} onChange={(e) => setForm({ ...form, recurrence_interval: e.target.value })}
                      data-testid="period-recurrence" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white">
                      <option value="quarterly">Quarterly</option>
                      <option value="semiannual">Semi-annual</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">Start date</label>
                  <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    data-testid="period-start" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Due date</label>
                  <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    data-testid="period-due" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-slate-500">Schools to request ({(form.school_ids || []).length}/{schools.length})</label>
                  <button type="button" className="text-xs text-blue-700 hover:underline"
                    onClick={() => setForm((f) => ({ ...f, school_ids: (f.school_ids || []).length === schools.length ? [] : schools.map((s) => s.tenant_id) }))}>
                    {(form.school_ids || []).length === schools.length ? "Clear all" : "Select all"}
                  </button>
                </div>
                <div className="mt-1 max-h-44 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100" data-testid="period-school-list">
                  {schools.map((s) => (
                    <label key={s.tenant_id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`period-school-${s.tenant_id}`}>
                      <input type="checkbox" checked={(form.school_ids || []).includes(s.tenant_id)} onChange={() => toggleSchool(s.tenant_id)} className="rounded border-slate-300" />
                      {s.school_name}
                    </label>
                  ))}
                  {schools.length === 0 && <div className="px-3 py-3 text-sm text-slate-400">No active schools. Add schools first.</div>}
                </div>
              </div>

              <label className={`flex items-start gap-3 rounded-xl border-2 px-4 py-3 cursor-pointer min-h-[44px] transition-colors ${form.enable_portfolio_assessments ? "border-[#6b3fa0] bg-[#f5f0fb]" : "border-slate-200 bg-white"}`} data-testid="period-portfolio-toggle-wrap">
                <input type="checkbox" checked={form.enable_portfolio_assessments}
                  onChange={(e) => setForm({ ...form, enable_portfolio_assessments: e.target.checked })}
                  data-testid="period-portfolio-toggle" className="mt-1 h-5 w-5 shrink-0 rounded border-slate-300 accent-[#6b3fa0]" />
                <span className="min-w-0">
                  <strong className="block text-[14px] text-slate-900">Also create my own assessment for each school</strong>
                  <span className="block text-[12.5px] text-slate-600 mt-0.5">Your private Portfolio view of each school, kept separate from the school's answers so you can compare them.</span>
                </span>
              </label>
            </div>

            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
              <button onClick={() => submitCreate(false)} disabled={!!busy} data-testid="period-create-submit"
                className="px-4 py-2 text-sm rounded-md border border-[#007a9e] text-[#00607d] hover:bg-[#e6f4f8] inline-flex items-center gap-2">
                {busy === "create" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create draft
              </button>
              <button onClick={() => submitCreate(true)} disabled={!!busy} data-testid="period-create-launch"
                className="px-4 py-2 text-sm rounded-md bg-[#007a9e] hover:bg-[#00607d] text-white inline-flex items-center gap-2">
                {busy === "launch-new" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Create &amp; launch now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
